<template>
  <div
    v-if="!userDisabled(identifier) && flashcard"
    class="question-of-the-day-container"
  >
    <div class="header-row">
      <dashboard-widget-header
        :title="'Question of the Day'"
        :show-close="userHideable"
        @remove-requested="removeRequested(identifier)"
      />
      <div class="scroll-arrows">
        <div
          class="scroll-arrow-container"
          :class="{ 'arrow-disabled': rowIsAtStart }"
          @click="scrollLeft"
        >
          <icon
            name="scroll-arrow-left"
            :class="{ 'arrow-disabled': rowIsAtStart }"
          />
        </div>
        <div
          class="scroll-arrow-container"
          :class="{ 'arrow-disabled': rowIsAtEnd }"
          @click="scrollRight"
        >
          <icon
            name="scroll-arrow-right"
            :class="{ 'arrow-disabled': rowIsAtEnd }"
          />
        </div>
      </div>
    </div>
    <div :ref="`widget-wrapper-${identifier}`" class="widget-wrapper">
      <question-of-the-day-card
        v-for="(qotd, index) in qotds"
        :key="qotd.date"
        :qotd="qotd"
        :index="index"
        :rate-limit-locked="rateLimitLocked"
        @click="handleClick(index)"
      >
      </question-of-the-day-card>
    </div>
  </div>
</template>

<script>
import DashboardWidgetHeader from '@components/dashboard-widgets/header';
import Icon from '@components/singletons/icon';
import QuestionOfTheDayCard from './QuestionOfTheDayCard.vue';
import RateLimitMixin from '@/mixins/rate-limit';
import WidgetMixin from '@/mixins/widget';
import { stripHtml } from '@/utils';
import { smoothScroll } from '@/utils/scrolling';
import { getDate } from '@/utils/date';

export default {
  components: {
    DashboardWidgetHeader,
    Icon,
    QuestionOfTheDayCard,
  },
  mixins: [WidgetMixin, RateLimitMixin],
  props: {
    dashboard: {
      type: Object,
    },
    config: {
      type: Object,
    },
    identifier: {
      type: Number,
    },
    userHideable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollPosition: 0,
      today: '',
      timer: null,
      widgetWidth: 0,
      widgetScrollWidth: 0,
    };
  },
  computed: {
    dateRange() {
      const first = 0;
      const last = 6;
      const range = [];
      for (let i = first; i <= last; i++) {
        range.push(getDate({ daysAgo: i }));
      }
      return range;
    },
    flashcard() {
      if (this.todaysFlashcardId) {
        return this.$store.getters['flashcards/flashcards'][this.todaysFlashcardId] || {};
      }
    },
    qotds () {
      const allQotds = this.$store.getters['questionsOfTheDay/all'];
      return this.dateRange
        .map(date => allQotds.find(q => q.date === date))
        .filter(q => q)
        .map(q => {
          let question = this.$store.getters['flashcards/flashcards'][q.v3FlashcardId]?.question;
          question = question ? stripHtml(question) : '';
          return {
            date: q.date,
            flashcardId: q.v3FlashcardId,
            categoryId: q.categoryId,
            question,
            flashcardState: this.$store.getters['flashcardStates/flashcardStates'][`${q.v3FlashcardId}-category-${q.categoryId}`],
          };
        });
    },
    todaysFlashcardId() {
      const todaysQotd = this.qotds.filter(q => q?.date === this.today)[0];
      return todaysQotd?.flashcardId;
    },
    rateLimitLocked() {
      if (this.$store.getters['session/hasPaid']) {
        return false;
      }
      return this.rateLimitingEnabled;
    },
    rowIsAtStart() {
      return this.scrollPosition === 0;
    },
    rowIsAtEnd() {
      return this.scrollPosition === this.widgetScrollWidth - this.widgetWidth;
    },
  },
  watch: {
    qotds: {
      handler() {
        this.$nextTick(this.updateWidgetWidth);
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateWidgetWidth();
      window.addEventListener('resize', this.updateWidgetWidth);
    });
    this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards', 'stats']);
    this.fetchQuestions();
    this.today = getDate();

    // Check every 10 minutes if the date has changed and fetch new questions
    if (!this.timer) {
      this.timer = setInterval(() => {
        if (this.today !== getDate()) {
          this.today = getDate();
          this.fetchQuestions();
        }
      }, 1000 * 60 * 10);
    }
  },
  beforeUnmount() {
    clearInterval(this.timer);
    window.removeEventListener('resize', this.updateWidgetWidth);
  },
  methods: {
    async fetchQuestions() {
      const { todaysQuestion } = await this.$store.dispatch('questionsOfTheDay/fetchLastWeekQuestionsOfTheDay');
      if (todaysQuestion) {
        this.$store.commit('flashcards/setFlashcards', { records: [todaysQuestion] });
      }
    },
    scrollLeft() {
      const widgetWrapperElement = this.$refs[`widget-wrapper-${this.identifier}`];
      let newScrollPosition = Math.max(0, widgetWrapperElement.scrollLeft - widgetWrapperElement.offsetWidth);

      // If the scroll position is close to the start, snap to the start
      if (newScrollPosition < 50) {
        newScrollPosition = 0;
      }
      smoothScroll(widgetWrapperElement, newScrollPosition);
      this.scrollPosition = newScrollPosition;
    },
    scrollRight() {
      const widgetWrapperElement = this.$refs[`widget-wrapper-${this.identifier}`];
      let newScrollPosition = Math.min(
        widgetWrapperElement.scrollWidth - widgetWrapperElement.offsetWidth,
        widgetWrapperElement.scrollLeft + widgetWrapperElement.offsetWidth,
      );

      // If the scroll position is close to the end, snap to the end
      if (widgetWrapperElement.scrollWidth - widgetWrapperElement.offsetWidth - newScrollPosition < 50) {
        newScrollPosition = widgetWrapperElement.scrollWidth - widgetWrapperElement.offsetWidth;
      }
      smoothScroll(widgetWrapperElement, newScrollPosition);
      this.scrollPosition = newScrollPosition;
    },
    updateWidgetWidth() {
      const widgetWrapperElement = this.$refs[`widget-wrapper-${this.identifier}`];
      if (widgetWrapperElement) {
        this.widgetWidth = widgetWrapperElement.offsetWidth;
        this.widgetScrollWidth = widgetWrapperElement.scrollWidth;
      }
    },
    handleClick(index) {
      if (this.rateLimitLocked && index !== 0) {
        return this.showUpSellModal();
      }
      this.$router.push({
        name: 'qotd-v2#show',
        params: {
          date: this.qotds[index].date,
        },
      });
    },
    showUpSellModal() {
      const source = { type: 'Question of the Day Widget' };
      const analyticParams = {
        dashboard: this.dashboard,
        destination: 'Upgrade Screen',
        widget: this.config,
      };
      this.$analytics.events.selectedRateLimitWidget(analyticParams);
      this.$store.dispatch('ui/toggleUpsellModal', { show: true, source });
    },
  },
};
</script>

<style scoped lang="scss">
.widget-wrapper {
  scrollbar-width: none; /* For Firefox */
  -ms-overflow-style: none; /* For Internet Explorer and Edge */
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: rem-calc(22);
  overflow-x: auto;
  overflow-y: hidden;
}

.widget-wrapper::-webkit-scrollbar {
  display: none; /* For Chrome, Safari, and Opera */
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: rem-calc(16);
  margin-bottom: rem-calc(2);
}

.scroll-arrows {
  display: flex;
  gap: rem-calc(8);
}

.scroll-arrow-container {
  cursor: pointer;
  line-height: rem-calc(24);
  text-align: center;
  background-color: #EAECF0;
  border-radius: 2rem;
  height: rem-calc(24);
  width: rem-calc(24);
}

.arrow-disabled {
  cursor: unset;

  img {
    opacity: 0.4;
  }
}
</style>
